import logger from 'apps/website/themes/inline_editor/logger';
import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';

import { editorConfig, beforeExecCommand, mobileToolbar } from './editorConfig';
import * as actions from '../helpers/actions';
import dispatchMessage from '../helpers/dispatchMessage';
import * as utils from '../helpers/textEditorUtils';

import './plugins/alignmentsplit';
import './plugins/builderFormatselect';
import './plugins/characterStyleSplit';
import './plugins/customFields';
import './plugins/builderFontsizeselect';

import { EditorInstanceType, AttributesType, SanitizeConfigType } from './RichTextEditor.types';

/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    tinymce: any;
  }
}

const CONTENT_CHANGE_DELAY = 500;
const CONTENT_CHANGE_MAX_WAIT = 1000;

class RichTextEditor {
  public editor: EditorInstanceType | null = null;

  private element: HTMLElement;

  private attributes: AttributesType;

  private editorActive: boolean;

  private savedContent: string | null;

  private debouncedHandleContentChange: () => void;

  private boundMouseEnterHandler: () => void;

  private boundMouseLeaveHandler: () => void;

  private boundClickHandler: (event: MouseEvent) => void;

  constructor(element: HTMLElement, attributes: AttributesType) {
    this.element = element;
    this.attributes = attributes;
    this.editor = null;
    this.editorActive = false;
    this.savedContent = null;

    this.debouncedHandleContentChange = debounce(
      this.handleContentChange.bind(this),
      CONTENT_CHANGE_DELAY,
      { leading: false, trailing: true, maxWait: CONTENT_CHANGE_MAX_WAIT },
    );

    this.boundMouseEnterHandler = this.mouseEnterHandler.bind(this);
    this.boundMouseLeaveHandler = this.mouseLeaveHandler.bind(this);
    this.boundClickHandler = this.clickHandler.bind(this);
  }

  init() {
    if (this.editor) return;
    if (!this.element) return;

    // Register click handler to scroll to the settings
    this.element.addEventListener('click', this.boundClickHandler);

    if (!utils.canEditInline(this.element, 'rich_text')) return;

    const globalTinyMCE = window.tinymce;
    if (!globalTinyMCE) {
      logger.error('TinyMCE is not loaded');
      return;
    }

    globalTinyMCE.init({
      target: this.element,
      inline: true,
      ...editorConfig(this.attributes),
      setup: (editor: any) => {
        editor.on('init', () => {
          this.editor = editor;
          this.savedContent = editor.getContent();
          logger.info('TinyMCE is initialized. Element:', this.attributes);
        });

        // If the screen is smaller than 880px, we need to change the toolbar.
        const width = window.innerWidth || document.documentElement.clientWidth;
        if (width <= 880) {
          editor.settings.toolbar = mobileToolbar(this.attributes.style);
        }

        editor.on('BeforeExecCommand', beforeExecCommand);
        editor.on('focus', () => {
          this.setEditorActive();
        });
        editor.on('blur', () => {
          this.handleContentChange();
          this.unsetEditorActive();
        });

        editor.on('change input compositionend setcontent', this.debouncedHandleContentChange);

        editor.on('dragover', (event: DragEvent) => {
          event.preventDefault();
          if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'none';
          }
          return false;
        });

        editor.on('drop', (event: DragEvent) => {
          event.preventDefault();
          return false;
        });
      },

      paste_preprocess: (_plugin: any, args: { content: string }) => {
        const config: SanitizeConfigType = {
          ...utils.sanitizeConfig('rich_text'),
          ALLOW_DATA_ATTR: false,
        };

        if (this.attributes.style === 'simple') {
          config.ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'a', 'br', 'p'];
          config.ALLOWED_ATTR = ['href', 'target', 'title'];
        }
        args.content = DOMPurify.sanitize(args.content, config);
      },
    });

    this.element.addEventListener('mouseenter', this.boundMouseEnterHandler);
    this.element.addEventListener('mouseleave', this.boundMouseLeaveHandler);

    utils.initElementAttributes(this.element);
  }

  remove() {
    if (this.editor) {
      this.editor.remove();
      this.editor = null;

      this.handleContentChange();
      this.unsetEditorActive();

      logger.info('TinyMCE is removed. Element:', this.attributes);
    }

    this.element.removeEventListener('mouseenter', this.boundMouseEnterHandler);
    this.element.removeEventListener('mouseleave', this.boundMouseLeaveHandler);
    this.element.removeEventListener('click', this.boundClickHandler);

    utils.removeEditorActiveAttributes(this.element);
  }

  setEditorActive() {
    this.editorActive = true;
    dispatchMessage(actions.setInlineEditingActive(this.attributes));
    utils.setEditorActiveAttributes(this.element);
  }

  unsetEditorActive() {
    if (this.editorActive) {
      this.editorActive = false;
      dispatchMessage(actions.unsetInlineEditingActive(this.attributes));
      utils.removeEditorActiveAttributes(this.element);
    }
  }

  handleContentChange() {
    if (!this.editor) return;
    if (!this.editorActive) return;

    const content = this.editor.getContent();
    // Prevent saving the same content multiple times
    if (content === this.savedContent) return;

    dispatchMessage(actions.updateContent({ ...this.attributes, content }));
    this.savedContent = content;
  }

  mouseEnterHandler() {
    utils.setEditorActiveAttributes(this.element);
  }

  mouseLeaveHandler() {
    if (!this.editorActive) {
      utils.removeEditorActiveAttributes(this.element);
    }
  }

  clickHandler(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.scrollToElementSettings();
  }

  scrollToElementSettings() {
    // scroll to the edited settings
    const settingsId = this.element.getAttribute(utils.ELEMENT_SETTINGS_ID_ATTRIBUTE);
    if (settingsId) {
      dispatchMessage(actions.editSetting(settingsId));
    }
  }
}

export default RichTextEditor;
